import { CommentatorScheduleData, ScheduleItem } from '../types/schedule';
import { normalizeTime } from './timeUtils';
import { getDayOfWeekFromDate } from './dateUtils';

const HEADER_MAP: Record<string, keyof ScheduleItem> = {
  shed: 'Shed',
  live: 'Live',
  ended: 'Ended',
  delay: 'Delay',
  cancel: 'Cancel',
  date: 'date',
  start: 'time',
  time: 'time',
  championship: 'championship',
  stage: 'stage',
  place: 'place',
  session: 'session',
  pc: 'PC',
  tg1: 'TG1',
  tg2: 'TG2',
  tg3: 'TG3',
  bcu1: 'BCU1',
  bcu2: 'BCU2',
  bcu3: 'BCU3',
  rt: 'RT',
  commentator1: 'Commentator1',
  commentator2: 'Commentator2',
  optionally: 'Optionally',
  duration: 'Duration',
  'live timing': 'LiveTiming',
  livetiming: 'LiveTiming',
  rutube: 'RuTube',
  videolink: 'VideoLink',
  spotter: 'Spotter',
};

// Улучшенный парсер CSV строки (обрабатывает кавычки)
export function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Экранированная кавычка
        current += '"';
        i++;
      } else {
        // Начало/конец кавычек
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Разделитель вне кавычек
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
}

// Валидация элемента расписания
function validateScheduleItem(item: Partial<ScheduleItem>): item is ScheduleItem {
  return !!(
    item.date &&
    item.time &&
    item.championship &&
    item.session
  );
}

export function parseCSV(text: string): ScheduleItem[] {
  const lines = text.split('\n').filter(Boolean);
  if (lines.length < 2) return [];
  
  const headers = parseCSVLine(lines[0]).map(h => h.trim());
  
  return lines.slice(1)
    .map(line => {
      const values = parseCSVLine(line);
      const obj: Partial<ScheduleItem> = {};
      headers.forEach((header, idx) => {
        const key = HEADER_MAP[header.toLowerCase()];
        if (!key) return;
        obj[key] = (values[idx] || '').trim() as any;
      });
      return obj;
    })
    .filter(validateScheduleItem)
    .map(item => ({
      ...item,
      time: normalizeTime(item.time),
      day: getDayOfWeekFromDate(item.date) // Вычисляем день недели из даты
    })) as ScheduleItem[];
}

const COMMENTATOR_NAME_COLUMN = 0;
const FIRST_TIME_COLUMN = 3;
const TIME_HEADER_PATTERN = /^\d{1,2}:\d{2}$/;

function isFilledScheduleCell(value: string | undefined): boolean {
  const normalized = (value ?? '').trim().toLowerCase();
  return normalized !== '' && normalized !== '0' && normalized !== 'false' && normalized !== 'нет';
}

export function parseCommentatorScheduleCSV(text: string): CommentatorScheduleData {
  const lines = text.split('\n').filter(line => line.trim() !== '');
  if (lines.length < 2) {
    return { times: [], rows: [] };
  }

  const headers = parseCSVLine(lines[0]).map(header => header.trim());
  const timeColumns = headers
    .map((header, index) => ({ header, index }))
    .filter(({ header, index }) => index >= FIRST_TIME_COLUMN && TIME_HEADER_PATTERN.test(header));

  const rows = lines.slice(1)
    .map(line => {
      const values = parseCSVLine(line);
      const commentator = (values[COMMENTATOR_NAME_COLUMN] ?? '').trim();
      if (!commentator) return null;

      return {
        commentator,
        slots: timeColumns.map(({ index }) => isFilledScheduleCell(values[index]))
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  return {
    times: timeColumns.map(({ header }) => header),
    rows
  };
}

